import { useState } from "react";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import { Box, Typography } from "@mui/material"
import InputPassword from "../InputPassword";
import BaseButton from "../BaseButton";
import BaseColorText from "../BaseColorText";

import { validationsUpdatePasswordSchema } from "../../validations/validationSignUpSchema";
import { updateUserPassword, logInWithEmailAndPassword } from "../../api-services/auth";
import { showToast } from "../../utils/toast";
import { FIREBASE_AUTH_ERRORS } from "../../constants/auth-errors";

const ChangePassword = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const auth = useSelector(state => state.auth)
  const { errors, handleBlur, handleChange, handleSubmit, touched, values, setValues, setErrors, setTouched } =
    useFormik({
      validateOnBlur: true,
      initialValues: {
        password: '',
        newPassword: '',
        confirmNewPassword: ''
      },
      validationSchema: validationsUpdatePasswordSchema,
      onSubmit: async (values, { setSubmitting }) => {
        setIsLoading(true);
        updateUserPassword(values.password, values.newPassword)
        .then((data) => {
          setErrorMessage(null)
          setIsLoading(false);
          setValues({
            password: '',
            newPassword: '',
            confirmNewPassword: ''
          })
          setErrors({
            password: '',
            newPassword: '',
            confirmNewPassword: ''
          })
          setTouched({
            password: false,
            newPassword: false,
            confirmNewPassword: false
          })
          logInWithEmailAndPassword(auth?.email, values.newPassword)
          return showToast({
            message: 'Your password has been updated!'
          })
        })
        .catch(function(error) {
          // An error happened.
          if (error.code === 'auth/wrong-password') {
            setErrorMessage({
              name: 'password',
              error: 'The current password is invalid.'
            })
          } else {
            setErrorMessage({
              name: '',
              error: FIREBASE_AUTH_ERRORS?.[error.code] || error.message
            })
          }
        })
        .finally(() => {
          setIsLoading(false)
        })
      }
    });

    const handleChangeInput = (e) => {
      const value = e.target.value;
      if (value !== values.newPassword) {
        setErrors({
          confirmNewPassword: 'New password does not match'
        })
      }
      handleChange(e)
    }

  return (
    <Box
      sx={{
        maxWidth: '600px',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Typography variant="h2">
        Change password
      </Typography>
      <Typography variant="body" color="primary.darkGray" mt="8px">
        To change your current password, please fill in the fields below. 
        (Any additional description, like Your password must contain 8 characters, one upper case letter, and a special character, etc) 
      </Typography>
      <Box
        sx={{
          mt: '32px'
        }}
      >
        <form onSubmit={handleSubmit}>
          <InputPassword
            fullWidth
            label='Current Password'
            name='password'
            placeholder='Enter your password'
            value={values.password || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            helperText={touched.password ? errors.password : ""}
            error={touched.password && Boolean(errors.password)}
            required
          />
          {errorMessage?.name === 'password' && 
            <Typography variant="subText">
              <BaseColorText color='#E73E3E' variant='subText'>{errorMessage?.error}</BaseColorText>
            </Typography>
          }
          <InputPassword
            sx={{
              mt: '24px',
            }}
            fullWidth
            label='New password'
            name='newPassword'
            placeholder='Create your new password'
            value={values.newPassword || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            helperText={touched.newPassword ? errors.newPassword : ""}
            error={touched.newPassword && Boolean(errors.newPassword)}
            required
          />
          <InputPassword
            sx={{
              mt: '24px',
            }}
            fullWidth
            label='Confirm new password'
            name='confirmNewPassword'
            placeholder='Confirm your new password'
            value={values.confirmNewPassword || ''}
            onChange={handleChangeInput}
            onBlur={handleBlur}
            helperText={errors.confirmNewPassword || ""}
            error={Boolean(errors.confirmNewPassword)}
            required
          />
          {(errorMessage && errorMessage?.name !== 'password') &&
            <Typography variant="subText">
              <BaseColorText color='#E73E3E' variant='subText'>{errorMessage?.error}</BaseColorText>
            </Typography>
          }
          <BaseButton
            loading={isLoading}
            disabled={isLoading || Object.entries(errors).length !== 0}
            variant='contained'
            buttonType='primary'
            type='submit'
            sx={{
              mt: '32px',
            }}
          >
            Change password
          </BaseButton>
        </form>
      </Box>
    </Box>
)}

export default ChangePassword